import React, { useState } from 'react'
import cn from 'classnames'
import styles from './upvalue.module.scss'
import { SignalType } from 'types/signals'
import EditableAmount from './EditableAmount'
import useStore from 'state/knovStore'
import api from 'api/api'
import { SATOSHI_UNIT, SATS_BEFORE_CONFIRM, USD_UNIT, toBitcoin, format } from 'lib/bsv-util'
import { useBsvExchangeRate, formatUsdValue, satsToUsd, usdToSats } from 'api/useBsvExchangeRate'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'
import {
    REACTION_OPTIONS,
    getCuratedReactionOption,
    loadAllReactionOptions,
    mergeReactionOptions,
    resolveReactionOption,
    searchReactionOptions,
    type ReactionOption,
} from './reactions'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Save, Check, X, Loader2 } from 'lucide-react'
import UpvalueGlyph from './UpvalueGlyph'
import UpvalueConfirmMessage, { UpvalueConfirmAction } from './UpvalueConfirmMessage'
import SatsIcon from 'components/shared/SatsIcon'
import Confirm from 'components/quests/Confirm'
import { DEFAULT_UPVALUE_EMOJI, isDefaultUpvalueEmoji } from './upvalueEmoji'
import {
    DEFAULT_DISPLAYED_RECENT_REACTIONS,
    fillRecentReactionOptionFallbacks,
    getRecentReactionEmojis,
    recentReactionDisplayCapacity,
} from './recentReactions'
import { LARGE_UPVALUE_CONFIRM_MODAL_KEY, SEED_INTRO_MODAL_KEY } from './upvalueModalKeys'
import {
    nextPressIsFreeReaction,
    upvaluesEnabledForAnswer,
    canSendFreeReactionForAnswer,
    persistPaidSignalIntroSeen,
    type ReactOrUpvalueOptions,
} from 'state/upvalueQueue'
import { shouldInterceptSeedIntro } from 'state/upvalueIntroVisibility'
import { seedMinimumSats, useSeedingContextForAnswer } from 'lib/seeding'
import {
    paidSignalDefault,
    paidSignalDefaultOptions,
    paidSignalOpeningSats,
} from 'lib/paidSignalDefaults'
import { savedPaidSignalType } from './signalOwnership'

const REACTION_GRID_COLUMNS = 6
const REACTION_GRID_ROW_HEIGHT = 54
const REACTION_GRID_VISIBLE_ROWS = 8

function buildPrioritizedReactionEmojis(answer: Answer, selectedReaction?: string) {
    const rankedEmojis = new Map<string, { amount: number; firstSeenIndex: number }>()

    for (const [index, upvalue] of (answer.bsv_upvalues || []).entries()) {
        const emoji = upvalue?.metadata?.sentiment_emoji || DEFAULT_UPVALUE_EMOJI
        const amount = parseFloat(String(upvalue.amount)) || 0
        const existingRank = rankedEmojis.get(emoji)

        if (existingRank) {
            existingRank.amount += amount
            continue
        }

        rankedEmojis.set(emoji, {
            amount,
            firstSeenIndex: index,
        })
    }

    const prioritizedEmojis = [
        selectedReaction?.trim(),
        ...Array.from(rankedEmojis.entries())
            .sort(([, leftRank], [, rightRank]) => {
                if (rightRank.amount !== leftRank.amount) {
                    return rightRank.amount - leftRank.amount
                }

                return leftRank.firstSeenIndex - rightRank.firstSeenIndex
            })
            .map(([emoji]) => emoji),
    ]

    const uniqueEmojis: string[] = []
    const seenEmojis = new Set<string>()

    for (const emoji of prioritizedEmojis) {
        if (!emoji || seenEmojis.has(emoji)) continue

        seenEmojis.add(emoji)
        uniqueEmojis.push(emoji)
    }

    return uniqueEmojis
}

const SATS_AMOUNT_PRESETS = [
    { label: '10K', value: 10_000, usdValue: null },
    { label: '100K', value: 100_000, usdValue: null },
    { label: '1M', value: 1_000_000, usdValue: null },
    { label: '10M', value: 10_000_000, usdValue: null },
    { label: '100M', value: 100_000_000, usdValue: null },
]

const USD_AMOUNT_PRESETS = [
    { label: '$0.001', dollars: 0.001 },
    { label: '$0.01', dollars: 0.01 },
    { label: '$0.10', dollars: 0.1 },
    { label: '$1.00', dollars: 1 },
    { label: '$10', dollars: 10 },
]

function normalizeUsdAmount(value: number | null | undefined) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null
    return value
}

function usdAmountsEqual(leftValue: number | null, rightValue: number | null) {
    if (leftValue == null || rightValue == null) return leftValue == null && rightValue == null
    return Math.abs(leftValue - rightValue) < 0.000000001
}

interface SignalOptionsProps {
    showBoost: boolean
    customUpvalueSats: number | null
    customUpvalueUsd?: number | null
    quickLockRef: React.MutableRefObject<HTMLDivElement | null>
    handleLockOrUpvalue: (
        sats: number,
        blocks?: number,
        type?: SignalType,
        options?: ReactOrUpvalueOptions,
    ) => void
    animateLock: (ref: React.MutableRefObject<HTMLDivElement | null>, scale?: number) => void
    answer: Answer
    signalQuest?: Quest
    selectedReaction?: string
    onReactionSelected?: (reaction: string) => void
    onReactionCommitted?: () => void
    initialSeed?: boolean
    onSeedChange?: (seed: boolean) => void
    closeModal?: () => void
    closeInlinePicker?: () => void
    variant?: 'modal' | 'inline'
}

export function SignalOptions({
    showBoost,
    customUpvalueSats,
    customUpvalueUsd,
    quickLockRef,
    handleLockOrUpvalue,
    animateLock,
    answer,
    signalQuest,
    selectedReaction,
    onReactionSelected,
    onReactionCommitted,
    initialSeed = false,
    onSeedChange,
    closeModal: closeModalProp,
    closeInlinePicker,
    variant = 'modal',
}: SignalOptionsProps) {
    const storeCloseModal = useStore(state => state.actions.modalActions.closeModal)
    const closeModal = closeModalProp || storeCloseModal
    const upvalueSats = useStore(state => state.upvalueSats)
    const upvalueUsd = useStore(state => state.upvalueUsd)
    const seedSats = useStore(state => state.seedSats)
    const seedUsd = useStore(state => state.seedUsd)
    const userBsvUnit = useStore(state => state.bsvUnit)
    const useSats = userBsvUnit === SATOSHI_UNIT
    const useUsd = userBsvUnit === USD_UNIT
    const signalType: SignalType = showBoost ? 'boost' : 'upvalue'
    const seedingContext = useSeedingContextForAnswer(answer, signalQuest)
    const [isSeed, setIsSeed] = useState(seedingContext.forceSeed || initialSeed)
    const [showSeedIntro, setShowSeedIntro] = useState(false)
    const [seedIntroAcknowledged, setSeedIntroAcknowledged] = useState(false)
    const seedIntroDontShowAgain = React.useRef(true)
    const requestedSeed = seedingContext.requestedSeed(isSeed)
    const savedUpvalueUsd = useUsd ? normalizeUsdAmount(upvalueUsd) : null
    const savedSeedUsd = useUsd ? normalizeUsdAmount(seedUsd) : null
    const paidSignalDefaults = {
        upvalueSats: upvalueSats ?? customUpvalueSats,
        upvalueUsd: savedUpvalueUsd,
        seedSats,
        seedUsd: savedSeedUsd,
    }
    const activeDefault = paidSignalDefault(requestedSeed, paidSignalDefaults)

    // Stream gating + free-first eligibility decide how this menu opens.
    const upvaluesEnabled = upvaluesEnabledForAnswer(answer)
    const freeReactionAvailable = canSendFreeReactionForAnswer(answer)
    // Arm Free when the answer can accept a free reaction and the menu is in a
    // free-first/free-only state; otherwise arm the user's saved upvalue amount.
    const openArmedSats = paidSignalOpeningSats({
        requestedSeed,
        freeReactionAvailable,
        upvaluesEnabled,
        nextPressIsFree: nextPressIsFreeReaction(answer),
        defaultSats: activeDefault.sats,
    })

    const [localSats, setLocalSats] = useState<number | null>(openArmedSats)
    const [savedSats, setSavedSats] = useState<number>(activeDefault.sats)
    const [localUsdAmount, setLocalUsdAmount] = useState<number | null>(activeDefault.usd)
    const [savedUsdAmount, setSavedUsdAmount] = useState<number | null>(activeDefault.usd)
    const [selectedEmoji, setSelectedEmoji] = useState(DEFAULT_UPVALUE_EMOJI)
    const [saveSuccess, setSaveSuccess] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<ReactionOption[]>([])
    const [allReactionOptions, setAllReactionOptions] = useState<ReactionOption[]>(REACTION_OPTIONS)
    const [prioritizedReactionOptions, setPrioritizedReactionOptions] = useState<ReactionOption[]>(
        [],
    )
    const [isCatalogLoading, setIsCatalogLoading] = useState(true)
    const [isSearchLoading, setIsSearchLoading] = useState(false)

    const normalizedLocalSats = Math.round(localSats || 0)
    const exchangeRate = useBsvExchangeRate({
        enabled: useUsd || normalizedLocalSats > 0 || requestedSeed,
    })
    const minimumSeedSats = requestedSeed ? seedMinimumSats() : null
    const normalizedSavedSats = Math.round(savedSats || 0)
    const isArmedFree = !requestedSeed && normalizedLocalSats < 1 && freeReactionAvailable
    const isAmountDirty =
        useUsd && (localUsdAmount != null || savedUsdAmount != null)
            ? !usdAmountsEqual(localUsdAmount, savedUsdAmount)
            : normalizedLocalSats !== normalizedSavedSats
    const normalizedSearchQuery = searchQuery.trim()

    React.useEffect(() => {
        let isActive = true

        loadAllReactionOptions()
            .then(options => {
                if (isActive) setAllReactionOptions(options)
            })
            .catch(error => {
                console.error('Failed to load reaction emoji catalog', error)
            })
            .finally(() => {
                if (isActive) setIsCatalogLoading(false)
            })

        return () => {
            isActive = false
        }
    }, [])

    React.useEffect(() => {
        // Track the persisted default for the Save dirty-check, but never push it
        // onto the armed amount — that would override an intentional Free (0) arm.
        setSavedSats(activeDefault.sats)
        setSavedUsdAmount(activeDefault.usd)
    }, [activeDefault.sats, activeDefault.usd])

    React.useEffect(() => {
        setLocalSats(currentSats => {
            const currentWholeSats = Math.round(currentSats || 0)
            if (currentWholeSats < 1 && !freeReactionAvailable) {
                if (useUsd && activeDefault.usd != null && exchangeRate) {
                    return usdToSats(activeDefault.usd, exchangeRate)
                }
                return activeDefault.sats
            }
            if (freeReactionAvailable && !upvaluesEnabled) {
                return 0
            }
            return currentSats
        })
    }, [
        activeDefault.sats,
        activeDefault.usd,
        exchangeRate,
        freeReactionAvailable,
        upvaluesEnabled,
        useUsd,
    ])

    React.useEffect(() => {
        if (!requestedSeed || minimumSeedSats == null) return

        setLocalSats(currentSats => Math.max(Math.round(currentSats || 0), minimumSeedSats))
        const minimumSeedUsd = satsToUsd(minimumSeedSats, exchangeRate)
        if (useUsd && minimumSeedUsd != null) {
            setLocalUsdAmount(currentUsd =>
                currentUsd == null ? null : Math.max(currentUsd, minimumSeedUsd),
            )
        }
    }, [exchangeRate, minimumSeedSats, requestedSeed, useUsd])

    React.useEffect(() => {
        if (!useUsd || localUsdAmount == null || !exchangeRate || isArmedFree) return

        const convertedSats = usdToSats(localUsdAmount, exchangeRate)
        const nextSats =
            requestedSeed && minimumSeedSats != null && convertedSats != null
                ? Math.max(convertedSats, minimumSeedSats)
                : convertedSats
        if (nextSats == null) return

        setLocalSats(currentSats =>
            Math.round(currentSats || 0) === nextSats ? currentSats : nextSats,
        )
    }, [exchangeRate, isArmedFree, localUsdAmount, minimumSeedSats, requestedSeed, useUsd])

    const saveButtonRef = React.useRef<HTMLButtonElement>(null)
    const searchInputRef = React.useRef<HTMLInputElement>(null)
    const recentReactionRowRef = React.useRef<HTMLDivElement>(null)
    const latestSearchRequestRef = React.useRef(0)
    const latestPriorityResolveRequestRef = React.useRef(0)
    const latestRecentResolveRequestRef = React.useRef(0)

    // Snapshot recents at open so the row doesn't reshuffle under the user.
    const [recentReactionHistory] = useState<string[]>(() => getRecentReactionEmojis())
    const [recentReactionCapacity, setRecentReactionCapacity] = useState(
        DEFAULT_DISPLAYED_RECENT_REACTIONS,
    )
    const recentReactionEmojis = React.useMemo(
        () => recentReactionHistory.slice(0, recentReactionCapacity),
        [recentReactionCapacity, recentReactionHistory],
    )

    React.useLayoutEffect(() => {
        const recentReactionRow = recentReactionRowRef.current
        if (!recentReactionRow || recentReactionHistory.length === 0) return

        const updateCapacity = () => {
            const nextCapacity = recentReactionDisplayCapacity(
                recentReactionRow.getBoundingClientRect().width,
            )

            setRecentReactionCapacity(currentCapacity =>
                currentCapacity === nextCapacity ? currentCapacity : nextCapacity,
            )
        }

        updateCapacity()

        if (typeof ResizeObserver === 'undefined') {
            if (typeof window === 'undefined') return

            window.addEventListener('resize', updateCapacity)
            return () => window.removeEventListener('resize', updateCapacity)
        }

        const resizeObserver = new ResizeObserver(updateCapacity)
        resizeObserver.observe(recentReactionRow)

        return () => resizeObserver.disconnect()
    }, [recentReactionHistory.length])

    const fallbackRecentReactionOptions = React.useMemo(
        () =>
            recentReactionEmojis.map(emoji => {
                const curatedReactionOption = getCuratedReactionOption(emoji)

                return curatedReactionOption || { emoji, label: emoji }
            }),
        [recentReactionEmojis],
    )
    const [recentReactionOptions, setRecentReactionOptions] = useState<ReactionOption[]>(
        fallbackRecentReactionOptions,
    )
    const displayedRecentReactionOptions = React.useMemo(
        () =>
            fillRecentReactionOptionFallbacks(recentReactionOptions, fallbackRecentReactionOptions),
        [fallbackRecentReactionOptions, recentReactionOptions],
    )

    React.useEffect(() => {
        if (recentReactionEmojis.length === 0) return

        const requestId = latestRecentResolveRequestRef.current + 1
        latestRecentResolveRequestRef.current = requestId

        Promise.all(recentReactionEmojis.map(emoji => resolveReactionOption(emoji)))
            .then(resolvedReactionOptions => {
                if (latestRecentResolveRequestRef.current !== requestId) return

                setRecentReactionOptions(
                    fillRecentReactionOptionFallbacks(
                        resolvedReactionOptions,
                        fallbackRecentReactionOptions,
                    ),
                )
            })
            .catch(error => {
                if (latestRecentResolveRequestRef.current !== requestId) return

                console.error('Failed to resolve recent reaction options', error)
                setRecentReactionOptions(fallbackRecentReactionOptions)
            })
    }, [fallbackRecentReactionOptions, recentReactionEmojis])

    const prioritizedReactionEmojis = React.useMemo(
        () => buildPrioritizedReactionEmojis(answer, selectedReaction),
        [answer, selectedReaction],
    )
    const fallbackPrioritizedReactionOptions = React.useMemo(
        () =>
            prioritizedReactionEmojis.map(emoji => {
                const curatedReactionOption = getCuratedReactionOption(emoji)

                return curatedReactionOption || { emoji, label: emoji }
            }),
        [prioritizedReactionEmojis],
    )
    const prioritizedReactionRankByEmoji = React.useMemo(
        () => new Map(prioritizedReactionEmojis.map((emoji, index) => [emoji, index] as const)),
        [prioritizedReactionEmojis],
    )

    const usdAmount =
        useUsd && localUsdAmount != null
            ? localUsdAmount
            : localSats === 0
            ? 0
            : satsToUsd(localSats, exchangeRate)
    const usdValue = formatUsdValue(usdAmount)
    const sendReactionAmountLabel = isArmedFree
        ? 'FREE'
        : useUsd && usdValue
        ? usdValue
        : format(normalizedLocalSats)
    const sendReactionUsdUsesPrefix = useUsd && !!usdValue && usdValue.startsWith('$')
    const sendReactionAmountText = sendReactionUsdUsesPrefix
        ? sendReactionAmountLabel.slice(1)
        : sendReactionAmountLabel
    const usdInlineValue = React.useMemo(() => {
        if (!usdValue) return undefined
        return usdValue.includes('$') ? usdValue : `$${usdValue}`
    }, [usdValue])
    const satsInlineValue = React.useMemo(() => {
        if (!useUsd || normalizedLocalSats < 1) return undefined
        return `≈ ${normalizedLocalSats.toLocaleString()} sats`
    }, [normalizedLocalSats, useUsd])

    React.useEffect(() => {
        if (selectedReaction) {
            setSelectedEmoji(selectedReaction)
        }
    }, [selectedReaction])

    React.useEffect(() => {
        if (prioritizedReactionEmojis.length === 0) {
            setPrioritizedReactionOptions([])
            latestPriorityResolveRequestRef.current += 1
            return
        }

        const requestId = latestPriorityResolveRequestRef.current + 1
        latestPriorityResolveRequestRef.current = requestId

        Promise.all(prioritizedReactionEmojis.map(emoji => resolveReactionOption(emoji)))
            .then(resolvedReactionOptions => {
                if (latestPriorityResolveRequestRef.current !== requestId) return

                setPrioritizedReactionOptions(
                    mergeReactionOptions(
                        resolvedReactionOptions,
                        fallbackPrioritizedReactionOptions,
                    ),
                )
            })
            .catch(error => {
                if (latestPriorityResolveRequestRef.current !== requestId) return

                console.error('Failed to resolve prioritized reaction options', error)
                setPrioritizedReactionOptions(fallbackPrioritizedReactionOptions)
            })
    }, [fallbackPrioritizedReactionOptions, prioritizedReactionEmojis])

    React.useEffect(() => {
        if (!normalizedSearchQuery) {
            setSearchResults([])
            setIsSearchLoading(false)
            latestSearchRequestRef.current += 1
            return
        }

        const requestId = latestSearchRequestRef.current + 1
        latestSearchRequestRef.current = requestId
        setIsSearchLoading(true)

        searchReactionOptions(normalizedSearchQuery)
            .then(results => {
                if (latestSearchRequestRef.current !== requestId) return

                setSearchResults(results)
            })
            .catch(error => {
                if (latestSearchRequestRef.current !== requestId) return

                console.error('Failed to search reaction options', error)
                setSearchResults([])
            })
            .finally(() => {
                if (latestSearchRequestRef.current !== requestId) return

                setIsSearchLoading(false)
            })
    }, [normalizedSearchQuery])

    const displayedReactionOptions = React.useMemo(() => {
        if (!normalizedSearchQuery) {
            const visiblePrioritizedOptions =
                prioritizedReactionOptions.length > 0
                    ? prioritizedReactionOptions
                    : fallbackPrioritizedReactionOptions

            return mergeReactionOptions(
                visiblePrioritizedOptions,
                REACTION_OPTIONS,
                allReactionOptions,
            )
        }

        return searchResults
            .map((option, index) => ({ option, index }))
            .sort((leftOption, rightOption) => {
                const leftRank = prioritizedReactionRankByEmoji.get(leftOption.option.emoji)
                const rightRank = prioritizedReactionRankByEmoji.get(rightOption.option.emoji)

                if (leftRank != null && rightRank != null) {
                    if (leftRank !== rightRank) {
                        return leftRank - rightRank
                    }
                } else if (leftRank != null) {
                    return -1
                } else if (rightRank != null) {
                    return 1
                }

                return leftOption.index - rightOption.index
            })
            .map(({ option }) => option)
    }, [
        allReactionOptions,
        fallbackPrioritizedReactionOptions,
        normalizedSearchQuery,
        prioritizedReactionOptions,
        prioritizedReactionRankByEmoji,
        searchResults,
    ])

    const reactionGridRef = React.useRef<HTMLDivElement>(null)
    const reactionRowCount = Math.ceil(displayedReactionOptions.length / REACTION_GRID_COLUMNS)
    const reactionGridVirtualizer = useVirtualizer({
        count: reactionRowCount,
        getScrollElement: () => reactionGridRef.current,
        estimateSize: () => REACTION_GRID_ROW_HEIGHT,
        overscan: 3,
    })
    const reactionGridHeight = Math.min(
        reactionGridVirtualizer.getTotalSize(),
        REACTION_GRID_ROW_HEIGHT * REACTION_GRID_VISIBLE_ROWS,
    )

    React.useEffect(() => {
        if (reactionGridRef.current) {
            reactionGridRef.current.scrollTop = 0
        }
    }, [normalizedSearchQuery])

    const handleSave = async () => {
        const wholeSats = normalizedLocalSats
        if (wholeSats >= 1) {
            const usdAmountToSave = useUsd ? normalizeUsdAmount(localUsdAmount) : null
            const defaultOptions = paidSignalDefaultOptions(requestedSeed, {
                sats: wholeSats,
                usd: usdAmountToSave,
            })

            try {
                await api.updateUserSpaceOptions({
                    ...defaultOptions,
                    signalType: 'upvalue',
                })

                useStore.setState({
                    ...defaultOptions,
                    signalType: savedPaidSignalType(signalType),
                })
                setSavedSats(wholeSats)
                setSavedUsdAmount(usdAmountToSave)

                setSaveSuccess(true)
                window.setTimeout(() => {
                    setSaveSuccess(false)
                }, 800)
            } catch (error) {
                console.error('Failed to save paid signal settings', error)
            }
        }
    }

    const amountPresets = React.useMemo(
        () =>
            [
                // Free is a first-class value level when this answer can accept it.
                ...(!requestedSeed && freeReactionAvailable
                    ? [{ label: 'FREE', value: 0, usdValue: null }]
                    : []),
                ...(useUsd
                    ? USD_AMOUNT_PRESETS.map(preset => ({
                          label: preset.label,
                          usdValue: preset.dollars,
                          value: usdToSats(preset.dollars, exchangeRate),
                      }))
                    : SATS_AMOUNT_PRESETS),
            ].filter(preset =>
                requestedSeed && minimumSeedSats != null
                    ? preset.value != null && preset.value >= minimumSeedSats
                    : true,
            ),
        [exchangeRate, freeReactionAvailable, minimumSeedSats, requestedSeed, useUsd],
    )

    const handlePresetAmount = (presetValue: number, presetUsdValue?: number) => {
        setLocalUsdAmount(useUsd ? normalizeUsdAmount(presetUsdValue) : null)
        setLocalSats(
            requestedSeed && minimumSeedSats != null
                ? Math.max(presetValue, minimumSeedSats)
                : presetValue,
        )
    }

    const handleClearSearch = () => {
        setSearchQuery('')
        setSearchResults([])
        setIsSearchLoading(false)
        latestSearchRequestRef.current += 1
        searchInputRef.current?.focus()
    }

    const handleSendReaction = (reaction: string) => {
        const enteredSats = Math.max(0, Math.round(localSats || 0))
        const sats =
            requestedSeed && minimumSeedSats != null
                ? Math.max(enteredSats, minimumSeedSats)
                : enteredSats
        const isFree = sats < 1
        // Nothing to send when the selected signal type is unavailable for this answer.
        if (isFree && !freeReactionAvailable) return
        if (!isFree && !upvaluesEnabled) return

        // Reaction-menu paid sends now use the shared upvalue/boost confirmation.
        // if (!isFree && !gon?.currentUser?.options?.has_seen_reaction_upvalue_intro) {
        //     useStore.setState({
        //         isModalOpen: true,
        //         modalParams: {
        //             type: 'ReactionUpvalueIntro',
        //             noHeader: true,
        //             customStyles: {
        //                 width: 'min(92dvw, 430px)',
        //                 maxWidth: '430px',
        //             },
        //         },
        //     })
        //     return
        // }

        setSelectedEmoji(reaction)
        onReactionSelected?.(reaction)
        const options: ReactOrUpvalueOptions =
            reaction && !isDefaultUpvalueEmoji(reaction)
                ? {
                      reactionEmoji: reaction,
                      source: 'signal_options',
                      isSeed: requestedSeed,
                      seedIntroAcknowledged,
                  }
                : {
                      source: 'signal_options',
                      isSeed: requestedSeed,
                      seedIntroAcknowledged,
                  }

        const commit = () => {
            // sats < 1 routes to a free reaction inside reactOrUpvalue.
            if (isFree && upvaluesEnabled) {
                setLocalSats(paidSignalDefaults.upvalueSats)
            }
            closeModal()
            handleLockOrUpvalue(sats, undefined, signalType, options)
            onReactionCommitted?.()
            setTimeout(
                () => {
                    animateLock(quickLockRef)
                },
                isFree ? 300 : 500,
            )
        }

        // Only confirm for genuinely large paid sends.
        if (!isFree && sats > SATS_BEFORE_CONFIRM) {
            useStore
                .getState()
                .actions.modalActions.openConfirmModal(
                    `${requestedSeed ? 'Seed' : 'Upvalue'} ${toBitcoin(sats)} bitcoin?`,
                    requestedSeed ? 'Seed' : 'Upvalue',
                    commit,
                    { modalKey: LARGE_UPVALUE_CONFIRM_MODAL_KEY, disableOverlayBlur: true },
                )
        } else {
            commit()
        }
    }

    const selectPaidSignalMode = (nextIsSeed: boolean) => {
        const nextRequestedSeed = seedingContext.requestedSeed(nextIsSeed)
        const nextDefault = paidSignalDefault(nextRequestedSeed, paidSignalDefaults)

        setIsSeed(nextIsSeed)
        setLocalSats(nextDefault.sats)
        setLocalUsdAmount(useUsd ? nextDefault.usd : null)
        setSavedSats(nextDefault.sats)
        setSavedUsdAmount(useUsd ? nextDefault.usd : null)
    }

    const handleSelectSeed = () => {
        selectPaidSignalMode(true)
        onSeedChange?.(true)
        if (seedIntroAcknowledged || !shouldInterceptSeedIntro(gon?.currentUser)) return

        seedIntroDontShowAgain.current = true
        setShowSeedIntro(true)
    }

    if (showSeedIntro) {
        return (
            <Confirm
                modalParams={{
                    message: <UpvalueConfirmMessage signalType={signalType} isSeed />,
                    confirmLabel: 'Seed',
                    onConfirm: () => {
                        setSeedIntroAcknowledged(true)
                        if (seedIntroDontShowAgain.current) {
                            persistPaidSignalIntroSeen(true)
                        }
                    },
                    headerTitle: 'Seed',
                    modalKey: SEED_INTRO_MODAL_KEY,
                    iconType: 'upvalue',
                    confirmVariant: 'bsv',
                    hideCancel: true,
                    inlineConfirmActions: true,
                    actionPrefix: (
                        <UpvalueConfirmAction
                            isSeed
                            onDontShowAgainChange={value => {
                                seedIntroDontShowAgain.current = value
                            }}
                        />
                    ),
                }}
                closeModal={() => setShowSeedIntro(false)}
            />
        )
    }

    return (
        <div
            className={cn(
                styles.signalOptionsContent,
                variant === 'modal' && styles.inModal,
                variant === 'inline' && styles.inlineSignalOptionsContent,
            )}
        >
            {variant === 'inline' && (
                <div className={styles.inlineSignalOptionsHeader}>
                    <button
                        type="button"
                        className={styles.inlineSignalOptionsCloseButton}
                        onClick={closeInlinePicker || closeModal}
                        aria-label="Close reactions"
                    >
                        <X size={16} aria-hidden />
                    </button>
                </div>
            )}
            <div className={styles.tabContent}>
                {(seedingContext.canChooseSeed || seedingContext.forceSeed) && (
                    <div className={styles.seedModePanel}>
                        {seedingContext.canChooseSeed ? (
                            <div
                                className={styles.seedModeToggle}
                                role="group"
                                aria-label="Paid signal type"
                            >
                                <button
                                    type="button"
                                    className={cn(
                                        styles.seedModeButton,
                                        !isSeed && styles.seedModeButtonActive,
                                    )}
                                    onClick={() => {
                                        selectPaidSignalMode(false)
                                        onSeedChange?.(false)
                                    }}
                                >
                                    Upvalue
                                </button>
                                <button
                                    type="button"
                                    className={cn(
                                        styles.seedModeButton,
                                        isSeed && styles.seedModeButtonActive,
                                        isSeed && styles.seedModeButtonSeedActive,
                                    )}
                                    onClick={handleSelectSeed}
                                >
                                    Seed
                                </button>
                            </div>
                        ) : (
                            <div className={styles.seedModeBadge}>Seeds</div>
                        )}
                        {requestedSeed ? (
                            <div className={styles.seedExplainer}>
                                Half to the author, half shared by earlier seeders.{' '}
                                {(answer.seed_count || 0) === 0
                                    ? "You're first — first seeds launch the post."
                                    : `${answer.seed_count} earlier ${
                                          answer.seed_count === 1
                                              ? 'seeder shares'
                                              : 'seeders share'
                                      } the curator half.`}
                            </div>
                        ) : null}
                    </div>
                )}
                {upvaluesEnabled && (
                    <div className={styles.reactionTopRow}>
                        <div
                            className={cn(
                                styles.reactionAmountCol,
                                requestedSeed && styles.seedArmed,
                            )}
                        >
                            <div className={styles.amountHeaderRow}>
                                <div className={styles.amountHeaderLabel}>Choose Amount</div>
                            </div>
                            <div
                                className={cn(
                                    styles.amountPresetRow,
                                    amountPresets.length > 5 && styles.amountPresetRowWithFree,
                                )}
                            >
                                {amountPresets.map(preset => (
                                    <button
                                        key={preset.label}
                                        type="button"
                                        className={cn(
                                            styles.amountPresetButton,
                                            preset.value != null &&
                                                preset.value > 0 &&
                                                preset.value === normalizedSavedSats &&
                                                styles.amountPresetButtonDefault,
                                            preset.value != null &&
                                                normalizedLocalSats === preset.value &&
                                                styles.amountPresetButtonSelected,
                                        )}
                                        disabled={preset.value == null}
                                        onClick={() => {
                                            if (preset.value != null) {
                                                handlePresetAmount(
                                                    preset.value,
                                                    preset.usdValue ?? undefined,
                                                )
                                            }
                                        }}
                                    >
                                        <span className={styles.amountPresetContent}>
                                            {preset.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <EditableAmount
                                value={localSats}
                                contextStyles={requestedSeed ? styles.seedAmountField : undefined}
                                useSats={useSats}
                                useUsd={useUsd}
                                onChange={value => {
                                    if (!useUsd) setLocalUsdAmount(null)
                                    setLocalSats(value)
                                }}
                                onUsdValueChange={setLocalUsdAmount}
                                subValue={useUsd ? satsInlineValue : usdInlineValue}
                                inlineAction={
                                    isArmedFree ? undefined : (
                                        <button
                                            ref={saveButtonRef}
                                            className={cn(
                                                styles.saveButton,
                                                styles.embeddedSaveButton,
                                                saveSuccess && styles.saveSuccess,
                                                isAmountDirty &&
                                                    !saveSuccess &&
                                                    styles.saveButtonDirty,
                                                signalType === 'boost'
                                                    ? styles.saveBoost
                                                    : styles.saveUpvalue,
                                            )}
                                            onClick={() => {
                                                void handleSave()
                                            }}
                                        >
                                            {saveSuccess ? (
                                                <Check
                                                    className={styles.saveButtonIcon}
                                                    size={16}
                                                    aria-hidden
                                                />
                                            ) : (
                                                <Save
                                                    className={styles.saveButtonIcon}
                                                    size={16}
                                                    aria-hidden
                                                />
                                            )}
                                            <span className={styles.saveQuickLockButtonLabel}>
                                                Save
                                            </span>
                                        </button>
                                    )
                                }
                            />
                        </div>
                    </div>
                )}

                <div className={styles.reactionSendLabel}>
                    <div className={styles.reactionSendHeaderRow}>
                        <span className={styles.reactionSendTitle}>
                            <span>{requestedSeed ? 'Seed' : 'Send'}</span>
                            {isArmedFree ? (
                                <span className={styles.reactionSendFreeAmount}>
                                    {sendReactionAmountLabel}
                                </span>
                            ) : (
                                <span className={styles.reactionSendAmount}>
                                    {sendReactionUsdUsesPrefix ? (
                                        <span className={styles.reactionSendUsdIcon}>$</span>
                                    ) : useUsd && usdValue ? null : (
                                        <SatsIcon
                                            showBars={true}
                                            contextStyles={styles.reactionSendSatsIcon}
                                        />
                                    )}
                                    <span>{sendReactionAmountText}</span>
                                </span>
                            )}
                            <span>Reaction</span>
                        </span>
                    </div>
                </div>
                {displayedRecentReactionOptions.length > 0 && (
                    <div className={styles.recentReactionsSection}>
                        <div className={styles.recentReactionsLabel}>Recently used</div>
                        <div ref={recentReactionRowRef} className={styles.recentReactionsRow}>
                            {displayedRecentReactionOptions.map(option => (
                                <button
                                    key={option.emoji}
                                    type="button"
                                    className={cn(
                                        styles.recentReactionOption,
                                        selectedEmoji === option.emoji &&
                                            styles.recentReactionOptionSelected,
                                    )}
                                    onClick={() => handleSendReaction(option.emoji)}
                                    aria-label={option.label}
                                    title={option.label}
                                >
                                    <UpvalueGlyph
                                        emoji={option.emoji}
                                        className={styles.recentReactionEmoji}
                                        size={22}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className={styles.reactionSearchFullWidth}>
                    <div
                        className={cn(
                            styles.reactionSearchInputWrap,
                            styles.reactionSearchInputWrapInline,
                        )}
                    >
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={event => setSearchQuery(event.target.value)}
                            placeholder="Search all emoji"
                            className={styles.reactionSearchInput}
                            aria-label="Search all emoji"
                        />
                        {isSearchLoading ? (
                            <Loader2
                                className={cn(
                                    styles.reactionSearchInputAction,
                                    styles.reactionSearchSpinner,
                                )}
                                size={16}
                                aria-hidden
                            />
                        ) : normalizedSearchQuery ? (
                            <button
                                type="button"
                                className={styles.reactionSearchClear}
                                onClick={handleClearSearch}
                                aria-label="Clear emoji search"
                            >
                                <X size={16} aria-hidden />
                            </button>
                        ) : null}
                    </div>
                </div>
                <div
                    ref={reactionGridRef}
                    className={styles.reactionGridViewport}
                    style={{ height: reactionGridHeight }}
                    role="group"
                    aria-label={
                        normalizedSearchQuery ? 'Emoji search results' : 'All emoji reactions'
                    }
                    aria-busy={!normalizedSearchQuery && isCatalogLoading}
                    data-emoji-count={displayedReactionOptions.length}
                >
                    <div
                        className={styles.reactionGridVirtualizer}
                        style={{ height: reactionGridVirtualizer.getTotalSize() }}
                    >
                        {reactionGridVirtualizer.getVirtualItems().map(virtualRow => (
                            <div
                                key={virtualRow.key}
                                className={styles.reactionGridRow}
                                style={{ transform: `translateY(${virtualRow.start}px)` }}
                            >
                                {displayedReactionOptions
                                    .slice(
                                        virtualRow.index * REACTION_GRID_COLUMNS,
                                        (virtualRow.index + 1) * REACTION_GRID_COLUMNS,
                                    )
                                    .map(option => (
                                        <button
                                            key={option.emoji}
                                            type="button"
                                            className={cn(
                                                styles.reactionOption,
                                                isDefaultUpvalueEmoji(option.emoji) &&
                                                    styles.reactionOptionUpvalue,
                                                selectedEmoji === option.emoji &&
                                                    styles.reactionOptionSelected,
                                            )}
                                            onClick={() => handleSendReaction(option.emoji)}
                                            aria-label={option.label}
                                            title={option.label}
                                        >
                                            <UpvalueGlyph
                                                emoji={option.emoji}
                                                className={styles.reactionEmoji}
                                                size={26}
                                            />
                                        </button>
                                    ))}
                            </div>
                        ))}
                    </div>
                </div>
                {normalizedSearchQuery && !isSearchLoading && searchResults.length === 0 && (
                    <div className={styles.reactionSearchEmptyState}>
                        No emoji matched that search.
                    </div>
                )}
            </div>
        </div>
    )
}
